import type { HexGrid, HexCell, HexCoord, MoveResult, CascadeStep } from "../types.ts";
import { coordToKey, getNeighbors } from "./hex.ts";

export function cloneGrid(grid: HexGrid): HexGrid {
  const cells = new Map<string, HexCell>();
  for (const [key, cell] of grid.cells) {
    cells.set(key, { ...cell, coord: { ...cell.coord } });
  }
  return { radius: grid.radius, cells, targetColor: grid.targetColor };
}

/**
 * Apply a player move: drop +1 into a cell, then process cascades.
 * Returns intermediate grid snapshots for animation.
 */
export function applyMove(grid: HexGrid, coord: HexCoord): MoveResult {
  const key = coordToKey(coord);
  const cell = grid.cells.get(key);

  if (!cell) {
    return { success: false, cascadeSteps: [], intermediateGrids: [], finalGrid: grid, won: false };
  }

  const newGrid = cloneGrid(grid);
  const target = newGrid.cells.get(key)!;
  target.count += 1;

  const cascadeSteps: CascadeStep[] = [];
  const intermediateGrids: HexGrid[] = [];

  // Process cascades via BFS
  const queue: string[] = [];
  if (target.count > target.capacity) {
    // Snapshot: state after +1, before any overflow
    intermediateGrids.push(cloneGrid(newGrid));
    queue.push(key);
  }

  const MAX_ITERATIONS = 200; // safety limit
  let iterations = 0;

  while (queue.length > 0 && iterations < MAX_ITERATIONS) {
    iterations++;
    const currentKey = queue.shift()!;
    const currentCell = newGrid.cells.get(currentKey);
    if (!currentCell) continue;

    // Only overflow if still over capacity
    if (currentCell.count <= currentCell.capacity) continue;

    const overflowColor = currentCell.color;
    const neighbors = getNeighbors(currentCell.coord);
    const affectedNeighbors: HexCoord[] = [];

    for (const nCoord of neighbors) {
      const nKey = coordToKey(nCoord);
      const nCell = newGrid.cells.get(nKey);
      if (!nCell) continue;

      // Claim neighbor with overflow color
      nCell.color = overflowColor;
      nCell.count += 1;
      affectedNeighbors.push(nCoord);

      // Queue if neighbor now overflows
      if (nCell.count > nCell.capacity) {
        queue.push(nKey);
      }
    }

    // Record cascade step
    cascadeSteps.push({
      coord: currentCell.coord,
      color: overflowColor,
      affectedNeighbors,
    });

    // Reset overflowed cell
    currentCell.count = 1;

    // Snapshot: state after this cascade step
    intermediateGrids.push(cloneGrid(newGrid));
  }

  return {
    success: true,
    cascadeSteps,
    intermediateGrids,
    finalGrid: newGrid,
    won: checkWin(newGrid),
  };
}

export function checkWin(grid: HexGrid): boolean {
  for (const cell of grid.cells.values()) {
    if (cell.color !== grid.targetColor) return false;
  }
  return true;
}
